import BaseUISystem from '../../BaseUISystem';
import Game from 'Game';
import { IAPReward } from 'Utility/RewardHelper';
import CrossPromoCampaign from 'Utility/CrossPromo/CrossPromoCampaign';
import { UIActions } from 'State/UI/UIActions';
import { IGameState } from 'State/Game/IGameState';
import { PopupID } from 'State/UI/PopupID';
import BaseMenu, { MenuState } from '../BaseMenu';
import { ShopLayout } from 'Layouts/Shop/ShopLayout';
import RelativePosition from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { GameObjectEvent } from 'GameObject/GameObject';
import { applyDefaultButtonAnimation } from '../../UIHelper';
import { ShopPopupItem } from './ShopPopupItem';
import Renderable from 'Component/Renderable';
import ShopGiftTimer from '../../../Timer/Timers/ShopGiftTimer';
import { ProductId } from 'Utility/Product';

type ShopPopupProps = {
    xPromoCampaign?: CrossPromoCampaign;
    onClose?: () => void;
};

export class ShopPopup extends BaseMenu {
    private IAPData: IAPReward[];
    private campaign?: CrossPromoCampaign;
    private onCloseCallback?: () => void;
    private layout: any;
    private items: ShopPopupItem[] = [];

    public async preload() {
        this.resources.set('IAPRewards', `resource/data/rewards/IAPRewards.json`);
        await super.preload();
    }

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.SHOP_MENU, uiSystem, false);
        new ShopGiftTimer();
    }

    public async onShow(params: ShopPopupProps): Promise<void> {
        super.onShow();

        const state: IGameState = Game.store.getState();
        const showStarterPack = state.playerState.purchases === 0;

        this.IAPData = Game.resource.get('IAPRewards');
        this.campaign = params?.xPromoCampaign;
        this.onCloseCallback = params?.onClose;

        this.layout = new ShopLayout();
        this.layout.root.addComponent(RelativePosition, { relativePosition: new Point(0.5, 0.5) });
        this.element.addChild(this.layout.root);

        this.layout.starterPack.getComponent(Renderable).visible = showStarterPack;
        this.layout.noAds.getComponent(Renderable).visible = showStarterPack;
        this.layout.jokerPack.getComponent(Renderable).visible = !showStarterPack;
        this.layout.specialOffer.getComponent(Renderable).visible = !showStarterPack;

        applyDefaultButtonAnimation(this.layout.closeButton);
        this.layout.closeButton.on(GameObjectEvent.SELECTED, this.handleCloseClick);

        if (!(await this.fetchCatalog())) {
            // There are not products to display user is offline.
            this.handleFailLoadingCatalog();
            return;
        }

        if (this.currentState === MenuState.CLOSED) return;

        this.items = [
            {
                productId: ProductId.COINS_10K,
                priceText: this.layout.pricePackA,
                buyButton: this.layout.buyButtonPackA,
                freeData: {
                    freeLevelIcon: this.layout.freeLevelIcon,
                    coinIcon: this.layout.coinIconPackA,
                    amountText: this.layout.amountPackA,
                },
            },
            {
                productId: ProductId.COINS_60k,
                priceText: this.layout.pricePackB,
                buyButton: this.layout.buyButtonPackB,
            },
            {
                productId: ProductId.COINS_120K,
                priceText: this.layout.pricePackC,
                buyButton: this.layout.buyButtonPackC,
            },
            {
                productId: ProductId.COINS_1mil_V2,
                priceText: this.layout.pricePackD,
                buyButton: this.layout.buyButtonPackD,
            },
            ...(showStarterPack
                ? [
                      {
                          productId: ProductId.STARTER_PACK_V3,
                          priceText: this.layout.priceStarterPack,
                          buyButton: this.layout.buyButtonStarterPack,
                          dragonbones: {
                              name: 'starter_pack_offer_popup',
                              parent: this.layout.starterPackAnimationContainer,
                          },
                          oldPrice: {
                              text: this.layout.starterPackOldPrice,
                              strike: this.layout.starterPackStrike,
                          },
                      },
                  ]
                : [
                      {
                          productId: ProductId.JOKER_DEAL_V2,
                          priceText: this.layout.priceJokerPack,
                          buyButton: this.layout.buyButtonJokerPack,
                      },
                      {
                          productId: ProductId.SPECIAL_OFFER,
                          priceText: this.layout.priceSpecialPack,
                          buyButton: this.layout.buyButtonSpecialPack,
                      },
                  ]),
        ].map(props => {
            const catalog = Game.sdk.getCatalog();
            const product = catalog.products.find(product => product.productId === props.productId);

            const iapReward = this.IAPData.find(reward => reward.id === props.productId);

            return new ShopPopupItem({ ...props, product, iapReward });
        });

        [
            this.layout.buyButtonPackA,
            this.layout.buyButtonPackB,
            this.layout.buyButtonPackC,
            this.layout.buyButtonPackD,
            this.layout.buyButtonJokerPack,
            this.layout.buyButtonSpecialPack,
            this.layout.buyButtonStarterPack,
        ].forEach(button => {
            applyDefaultButtonAnimation(button);
        });
    }

    private async fetchCatalog(): Promise<boolean> {
        if (Game.sdk.getCatalog().products.length > 0) return true;

        await Game.sdk.fetchCatalog();
        return Game.sdk.getCatalog().products.length > 0;
    }

    private handleFailLoadingCatalog() {
        this.layout.popup.getComponent(Renderable).visible = false;
        this.layout.noProductsContainer.getComponent(Renderable).visible = true;
    }

    private readonly handleCloseClick = (): void => {
        UIActions.hidePopup(PopupID.SHOP_MENU);

        if (this.campaign) {
            UIActions.showPopup(PopupID.CROSS_PROMO_MENU, this.campaign);
        }

        this.onCloseCallback?.();
    };

    public handleStateChanged(state: IGameState): void {
        this.items.forEach(item => {
            item.handleStateChanged(state);
        });
    }
}
